// Mirrors the API's helpers/discount.js so forms can show live totals.
// A discount is a percentage of the subtotal ('percentage') or a fixed dollar
// amount ('fixed'). The API recomputes and validates everything on save.

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Live totals for a form. `error` is set when the value can't be saved.
export const calcDiscount = (subtotal, type, value) => {
  const base = round2(parseFloat(subtotal) || 0);
  const v = parseFloat(value) || 0;
  if (v < 0) return { amount: 0, total: base, error: 'Discount cannot be negative.' };
  if (v === 0) return { amount: 0, total: base, error: null };

  if (type === 'fixed') {
    if (v > base) return { amount: 0, total: base, error: 'Discount cannot be more than the subtotal.' };
    return { amount: round2(v), total: round2(base - v), error: null };
  }
  if (v > 100) return { amount: 0, total: base, error: 'Discount percentage cannot be more than 100%.' };
  const amount = round2(base * v / 100);
  return { amount, total: round2(base - amount), error: null };
};

// Request fields for a discount entered as (type, value).
export const discountPayload = (type, value) => {
  const v = parseFloat(value) || 0;
  if (v <= 0) return { discountType: 'percentage', discountPercentage: null, discountAmount: null };
  return type === 'fixed'
    ? { discountType: 'fixed', discountPercentage: null, discountAmount: v }
    : { discountType: 'percentage', discountPercentage: v, discountAmount: null };
};

// The (type, value) a form should start with for a saved estimate / CO.
export const discountFormValue = (record) => {
  if (record?.discountType === 'fixed') {
    return { type: 'fixed', value: parseFloat(record.discountAmount) ? String(parseFloat(record.discountAmount)) : '' };
  }
  const pct = parseFloat(record?.discountPercentage) || 0;
  return { type: 'percentage', value: pct ? String(pct) : '' };
};

// What to display for a saved estimate / CO. `base` is the subtotal the
// discount applies to (originalSubtotal ?? subtotal for estimates, so approved
// change orders don't inflate it). Rows saved before fixed discounts existed
// have no discountAmount, so it's derived from the percentage.
export const discountInfo = (record, base) => {
  const pct = parseFloat(record?.discountPercentage) || 0;
  const type = record?.discountType || 'percentage';
  const amount = record?.discountAmount != null
    ? parseFloat(record.discountAmount) || 0
    : round2((parseFloat(base) || 0) * pct / 100);
  return {
    hasDiscount: amount > 0,
    type,
    percentage: pct || null,
    amount,
    label: type === 'percentage' && pct ? `Discount (${pct}%)` : 'Discount',
    reason: record?.discountReason || '',
  };
};
